/**
 * Refunds: the pure bits (maths, labels, copy, reading API shapes).
 *
 * The contract is beu-node-v2 docs/refunds.md. The engine and the finance
 * reporting are built in parallel with this screen, so everything here reads
 * defensively: a field that isn't there means "no refunds", never an error.
 * Every shape we assume is named in a comment where it is read.
 *
 * Copy rules: plain British English, short, no em or en dashes. A negative
 * amount uses the minus sign (U+2212), as the Overview does.
 */

export const MINUS = '−';

const num = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;

/** £1,234.50 (finance views' format). Negative reads −£12.00. */
export function money(v) {
  const n = round2(v);
  const abs = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? MINUS : ''}£${abs}`;
}

/** −£12.00 for an amount taken off (the amount itself may come in positive or negative). */
export const minusMoney = (v) => `${MINUS}£${Math.abs(round2(v)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Refund status and badges ──────────────────────────────────────

/**
 * 'none' | 'partial' | 'full' for a sale. Reads `refundStatus` (the booking
 * field, spec Decision 1). Older payloads without it: derived from
 * `refundedTotal` against the sale's total when both are there.
 */
export function refundStatusOf(sale) {
  if (!sale) return 'none';
  const s = String(sale.refundStatus || '').toLowerCase();
  if (s === 'full' || s === 'refunded') return 'full';
  if (s === 'partial' || s === 'part_refunded' || s === 'partially_refunded') return 'partial';
  if (s === 'none') return 'none';
  const refunded = Math.abs(num(sale.refundedTotal));
  if (refunded > 0) {
    const total = num(sale.total != null ? sale.total : sale.amount);
    return total > 0 && refunded >= total - 0.005 ? 'full' : 'partial';
  }
  return 'none';
}

/** Badge for a status: { label, tone } or null (nothing refunded). */
export function refundBadge(status) {
  if (status === 'partial') return { label: 'Part refunded', tone: 'partial' };
  if (status === 'full') return { label: 'Refunded', tone: 'full' };
  return null;
}

/** Badge colours, matching the finance status pills. */
export const BADGE_COLOURS = {
  partial: { background: '#fef3c7', color: '#b45309' },
  full: { background: '#f3f4f6', color: '#4b5563' },
};

/** Can this sale still be refunded from here? (completed and not fully refunded) */
export function canOfferRefund(sale, { completed }) {
  return !!completed && refundStatusOf(sale) !== 'full';
}

/**
 * A negative refund entry in the transactions list (spec: "separate negative
 * refund rows dated when made"). Assumed marked by type/kind 'refund' or
 * isRefund; the sale it belongs to rides as bookingId / saleId.
 */
export function isRefundEntry(t) {
  if (!t) return false;
  const type = String(t.type || t.kind || t.entryType || '').toLowerCase();
  return type === 'refund' || t.isRefund === true;
}

/** The sale a refund entry belongs to (opens its detail). */
export const refundEntrySaleId = (t) => (t && (t.bookingId || t.saleId || t.refundOf || t.parentId)) || null;

// ── Totals on finance responses ───────────────────────────────────

/**
 * Refund totals from whatever the reporting backend sends: a number (with an
 * optional separate count), or { count, amount } (Overview contract), or the
 * reporting block { count, amount, salesAmount, ... }. `salesAmount` (the part
 * off takings, tips excluded) wins over `amount` so Refunds and Net takings
 * always add up to the sales (refunds/refundReporting: net = sales −
 * salesAmount). Always a positive amount, or null when there were none.
 */
export function refundTotals(v, countHint) {
  if (v == null || v === false) return null;
  let amount;
  let count = null;
  if (typeof v === 'number' || typeof v === 'string') {
    amount = num(v);
    count = countHint != null && Number.isFinite(Number(countHint)) ? Number(countHint) : null;
  } else if (typeof v === 'object') {
    amount = num(v.salesAmount != null ? v.salesAmount : (v.amount != null ? v.amount : (v.total != null ? v.total : v.value)));
    const c = v.count != null ? v.count : (v.refunds != null ? v.refunds : countHint);
    count = c != null && Number.isFinite(Number(c)) ? Number(c) : null;
  } else {
    return null;
  }
  amount = Math.abs(round2(amount));
  return amount > 0 ? { amount, count } : null;
}

/** First source that carries refunds. */
export function firstRefundTotals(...sources) {
  for (const s of sources) {
    const r = refundTotals(s);
    if (r) return r;
  }
  return null;
}

/** Net takings: the server's figure when it sends one, else gross − refunds. */
export function netAfterRefunds(gross, refunds, explicitNet) {
  if (explicitNet != null && Number.isFinite(Number(explicitNet))) return round2(explicitNet);
  return round2(num(gross) - (refunds ? refunds.amount : 0));
}

/** "3 refunds" / "1 refund" / '' */
export function refundCountLabel(count) {
  if (count == null || !(count > 0)) return '';
  return `${count} ${count === 1 ? 'refund' : 'refunds'}`;
}

/**
 * Refunds on a report's data. Reads data.refunds (revenue summary), then
 * data.totals.refunds + refundCount (sales by staff, staff earnings), then
 * data.summary.totalRefunds + refundCount (end of day) or summary.refunds.
 * The VAT report's own `refunded` block is NOT read here: it already shows
 * refunds its own way.
 */
export function reportRefunds(reportType, data) {
  if (!data || reportType === 'vat-report') return null;
  const t = data.totals || {};
  const sm = data.summary || {};
  return refundTotals(data.refunds)
    || refundTotals(t.refunds, t.refundCount)
    || refundTotals(sm.totalRefunds, sm.refundCount)
    || refundTotals(sm.refunds, sm.refundCount);
}

/** Net takings on a report, when the server sends one (netTakings, or end of day's summary.netRevenue). */
export function reportNetTakings(data) {
  if (!data) return null;
  const t = data.totals || {};
  const sm = data.summary || {};
  const v = data.netTakings != null ? data.netTakings
    : (t.netTakings != null ? t.netTakings
      : (sm.netTakings != null ? sm.netTakings : (sm.netRevenue != null ? sm.netRevenue : null)));
  return v != null && Number.isFinite(Number(v)) ? round2(v) : null;
}

/** Refunds on one row (payment method, staff member): refunds (+ refundCount) / refunded / refundAmount / refundTotal. */
export function rowRefunds(row) {
  if (!row) return null;
  return refundTotals(row.refunds, row.refundCount) || firstRefundTotals(row.refunded, row.refundAmount, row.refundTotal);
}

/**
 * What refunds did to a commission balance, SIGNED as the server sends it:
 * `refundAdjustment` (negative when cash handed back takes the salon's share
 * off what's owed; positive when a restored gift card shrinks the credit).
 * Older names are read as a reduction.
 */
export function commissionRefund(row) {
  if (!row) return 0;
  if (row.refundAdjustment != null) return round2(row.refundAdjustment);
  const v = row.refundCommission != null ? row.refundCommission
    : (row.commissionRefunded != null ? row.commissionRefunded
      : (row.refunds && typeof row.refunds === 'object' && !Array.isArray(row.refunds) && row.refunds.commission != null ? row.refunds.commission : 0));
  const a = Math.abs(round2(v));
  return a ? -a : 0;
}

/** +£2.00 / −£2.00 */
export const signedMoney = (v) => (round2(v) < 0 ? minusMoney(v) : `+${money(v)}`);

// ── The refund panel: lines, amounts, request bodies ──────────────

export const LINE_KIND_LABEL = { service: 'Service', product: 'Product', voucher: 'Gift card', tip: 'Tip', amount: 'Amount' };

/**
 * Lines on a preview. Assumed row shape (spec's refunds[].lines plus what was
 * paid): { kind, index, itemId, name, amount|paid, refunded, refundable, qty }.
 * `refundable` = line paid − already refunded on that line (spec Money rules).
 */
export function normaliseLines(lines) {
  if (!Array.isArray(lines)) return [];
  return lines.map((l, i) => {
    const kind = String(l.kind || l.type || 'service').toLowerCase();
    const index = l.index != null ? l.index : i;
    const paid = round2(l.paid != null ? l.paid : (l.price != null ? l.price : l.amount));
    const refunded = Math.abs(round2(l.refunded != null ? l.refunded : l.alreadyRefunded));
    const refundable = round2(l.refundable != null ? l.refundable : Math.max(0, paid - refunded));
    return {
      key: `${kind}:${index}`,
      kind,
      index,
      itemId: l.itemId || l.id || null,
      name: l.name || LINE_KIND_LABEL[kind] || 'Item',
      paid,
      refunded,
      refundable: Math.max(0, refundable),
      qty: l.qty != null ? Number(l.qty) : (l.quantity != null ? Number(l.quantity) : null),
      restockable: kind === 'product' && l.restockable !== false,
      // A loyalty reward (paymentType 16): never paid in money, nothing to refund.
      comped: !!l.comped,
    };
  });
}

/** Sum of what's refundable on the chosen lines. */
export function selectedTotal(lines, selected) {
  return round2((lines || []).reduce((s, l) => s + (selected && selected[l.key] ? l.refundable : 0), 0));
}

/** Everything still refundable on the sale. */
export const refundableTotal = (lines) => round2((lines || []).reduce((s, l) => s + l.refundable, 0));

/** "12.5", "£12.50", "1,200" → a number rounded to pence, or null. */
export function parseAmount(input) {
  if (input == null) return null;
  const s = String(input).replace(/[£,\s]/g, '');
  if (!s || !/^\d*\.?\d*$/.test(s) || s === '.') return null;
  const n = round2(parseFloat(s));
  return Number.isFinite(n) ? n : null;
}

/** '' when fine, else a short message. */
export function amountError(amount, max) {
  if (amount == null || !(amount > 0)) return 'Enter an amount';
  if (max != null && amount > round2(max) + 0.004) return `The most you can refund is ${money(max)}`;
  return '';
}

/**
 * The body for POST /preview and POST /. Matches the engine's buildLines
 * (beu-node-v2 refund/refundMaths.js): lines OR an amount, never both, and
 * the server works out each line's amount and quantity itself.
 *   whole  → { restock } only: the server refunds everything left (including
 *            money no longer tied to a line after an amount refund). Only when
 *            the products' stock choices differ are the lines listed, each
 *            with its own restock.
 *   lines  → the chosen lines, each { kind, index } (+ restock for products).
 *   amount → { amount }.
 * `methodSplit` is { method: amount } and only sent when staff override how
 * it goes back; it must add up to the refund.
 */
export function buildRefundBody({ bookingId, mode, lines, selected, restock, amount, methodSplit }) {
  const body = { bookingId };
  const pool = (lines || []).filter((l) => l.refundable > 0);
  const lineRow = (l) => (l.kind === 'product' ? { kind: l.kind, index: l.index, restock: !!(restock && restock[l.key]) } : { kind: l.kind, index: l.index });
  if (mode === 'amount') {
    body.amount = round2(amount);
  } else if (mode === 'lines') {
    const chosen = pool.filter((l) => selected && selected[l.key]);
    if (chosen.length) body.lines = chosen.map(lineRow);
  } else {
    const products = pool.filter((l) => l.kind === 'product');
    const on = products.filter((l) => restock && restock[l.key]).length;
    if (products.length && on > 0 && on < products.length) body.lines = pool.map(lineRow);
    else if (products.length) body.restock = on === products.length;
  }
  if (methodSplit && typeof methodSplit === 'object' && Object.keys(methodSplit).length) body.methodSplit = methodSplit;
  return body;
}

/** Stable per attempt, so a retried POST can never refund twice. */
export function makeIdempotencyKey(bookingId) {
  const rnd = Math.random().toString(36).slice(2, 10);
  return `rf_${bookingId || 'sale'}_${Date.now().toString(36)}_${rnd}`;
}

// ── Reading a preview ─────────────────────────────────────────────

export const METHOD_LABEL = { card: 'Card', cash: 'Cash', voucher: 'Gift card', other: 'Other' };

const methodKey = (m) => {
  const s = String(m || '').toLowerCase();
  if (s === 'gift_card' || s === 'giftcard' || s === 'gift card') return 'voucher';
  return s || 'other';
};

/** [{ method, label, amount, last4 }] from preview.methods (array, or { card: 10, cash: 5 }). */
export function normaliseMethods(methods) {
  if (!methods) return [];
  const rows = Array.isArray(methods)
    ? methods.map((m) => ({ method: methodKey(m.method || m.type), amount: round2(m.amount), last4: m.last4 || null, label: m.label || null }))
    : Object.keys(methods).map((k) => ({ method: methodKey(k), amount: round2(methods[k] && typeof methods[k] === 'object' ? methods[k].amount : methods[k]), last4: null, label: null }));
  return rows.filter((r) => Math.abs(r.amount) > 0).map((r) => ({ ...r, amount: Math.abs(r.amount), label: r.label || METHOD_LABEL[r.method] || 'Other' }));
}

/** "Card ending 4242" / "Cash" / "Gift card" */
export const methodLineLabel = (m) => (m.method === 'card' && m.last4 ? `Card ending ${m.last4}` : m.label);

const isSalonRole = (r) => /salon|business|owner/i.test(String(r || ''));

/**
 * Who the money comes back from: [{ userId, name, role, amount, how }] from
 * preview.reversals ({ userId, name, amount, how: 'reverse'|'debt'|'held' }).
 * Several legs of one person (a split card sale) are added together.
 */
export function normaliseRecipients(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  rows.forEach((r) => {
    const row = { userId: r.userId || null, name: r.name || r.userName || null, role: isSalonRole(r.role || r.kind) ? 'salon' : 'stylist', amount: Math.abs(round2(r.amount)), how: r.how || null };
    if (!(row.amount > 0)) return;
    const same = row.userId ? out.find((x) => x.userId === row.userId && x.how === row.how) : null;
    if (same) same.amount = round2(same.amount + row.amount);
    else out.push(row);
  });
  return out;
}

/** "Jo", "The salon", "Jo · from their next payout" (a leg already paid out comes off their next one). */
export const recipientLabel = (r) => {
  const who = r.role === 'salon' ? 'The salon' : (r.name || 'The stylist');
  return r.how === 'debt' ? `${who} · from their next payout` : who;
};

/**
 * The fees on the refunded part that don't come back, borne by the seller:
 * preview.fee = { amount, borneBy: { userId, name }, stripeFee, beuFee }.
 * Salon sale → 'salon'; a self-employed sale → that stylist, by name.
 * Returns { amount, borneBy: 'salon'|'stylist', name, stripeFee, beuFee } or null.
 */
export function normaliseFee(fee, seller) {
  if (fee == null) return null;
  const obj = typeof fee === 'object' ? fee : {};
  const amount = Math.abs(round2(typeof fee === 'object' ? fee.amount : fee));
  if (!(amount > 0)) return null;
  const by = obj.borneBy || obj.paidBy || obj.from || obj.fromRole;
  const byName = (by && typeof by === 'object' ? by.name : null) || obj.name || obj.fromName || null;
  let borneBy;
  if (seller && seller.kind) borneBy = seller.kind === 'self_employed' ? 'stylist' : 'salon';
  else if (by && typeof by === 'string') borneBy = isSalonRole(by) ? 'salon' : 'stylist';
  else borneBy = 'salon';
  const part = (v) => (v != null && Number.isFinite(Number(v)) ? Math.abs(round2(v)) : null);
  return { amount, borneBy, name: borneBy === 'stylist' ? byName : null, stripeFee: part(obj.stripeFee), beuFee: part(obj.beuFee) };
}

/**
 * "The £0.30 card fee and £0.50 Beu fee aren't returned. The salon covers them as the seller."
 * or, without the split, "£0.80 in fees isn't returned. ..."
 */
export function feeLine(fee) {
  if (!fee) return '';
  const who = fee.borneBy === 'salon' ? 'The salon' : (fee.name || 'The stylist');
  if (fee.stripeFee > 0 && fee.beuFee > 0) return `The ${money(fee.stripeFee)} card fee and ${money(fee.beuFee)} Beu fee aren't returned. ${who} covers them as the seller.`;
  if (fee.stripeFee > 0 && !(fee.beuFee > 0)) return `The ${money(fee.stripeFee)} card fee isn't returned. ${who} covers it as the seller.`;
  return `${money(fee.amount)} in fees isn't returned. ${who} covers it as the seller.`;
}

/** "2 items back in stock" from the chosen product lines. */
export function stockLine(lines, restock, mode, selected) {
  if (mode === 'amount') return '';
  const items = (lines || []).filter((l) => l.kind === 'product' && l.refundable > 0 && (mode !== 'lines' || (selected && selected[l.key])) && restock && restock[l.key]);
  const qty = items.reduce((s, l) => s + (l.qty > 0 ? l.qty : 1), 0);
  if (!qty) return '';
  return `${qty} ${qty === 1 ? 'item goes' : 'items go'} back in stock`;
}

/** "3 stamps and 120 points taken back" */
export function loyaltyLine(loyalty) {
  if (!loyalty) return '';
  const s = Math.abs(num(loyalty.stampsReversed != null ? loyalty.stampsReversed : loyalty.stamps));
  const p = Math.abs(num(loyalty.pointsReversed != null ? loyalty.pointsReversed : loyalty.points));
  const parts = [];
  if (s) parts.push(`${s} ${s === 1 ? 'stamp' : 'stamps'}`);
  if (p) parts.push(`${p} ${p === 1 ? 'point' : 'points'}`);
  return parts.length ? `${parts.join(' and ')} taken back` : '';
}

const SE_KINDS = /self.?employed|^se$|^stylist$/i;

/** { kind: 'salon'|'self_employed', userId, name } or null. Assumed preview.seller. */
export function normaliseSeller(seller) {
  if (!seller) return null;
  if (typeof seller === 'string') return { kind: SE_KINDS.test(seller) ? 'self_employed' : 'salon', userId: null, name: null };
  const kind = SE_KINDS.test(String(seller.kind || seller.type || seller.role || '')) || seller.isSelfEmployed === true ? 'self_employed' : 'salon';
  return { kind, userId: seller.userId || seller._id || seller.id || null, name: seller.name || null };
}

const CODEISH = /^[a-z][a-z0-9_.-]*$/i;

/** One preview in the panel's terms. */
/**
 * One preview in the panel's terms. The engine's POST /preview answers
 * (refund/refundFacade.preview):
 *   { allowed, code, reason (a sentence), canRequest, seller {kind,userId,name},
 *     refundStatus, refundedTotal,
 *     sale: { paid, refunded, refundable, byMethod: { card: {paid,refunded,refundable}, ... },
 *             lines: [{ kind, index, itemId, name, qty, paid, refunded, refundable, comped }] },
 *     total, methods: { card, cash, voucher, other }, rows, reversals, fee, tax, loyalty, stock, after }
 * `sale.lines` are the sale's lines (what can be chosen); top-level `lines`
 * are this refund's lines.
 */
export function normalisePreview(raw) {
  const p = raw || {};
  const sale = p.sale && typeof p.sale === 'object' ? p.sale : null;
  const reasonIsCode = typeof p.reason === 'string' && CODEISH.test(p.reason) && !/\s/.test(p.reason);
  const lines = normaliseLines(sale && Array.isArray(sale.lines) ? sale.lines : p.lines);
  const amount = round2(p.total != null ? p.total : (p.amount != null ? p.amount : p.refundAmount));
  const seller = normaliseSeller(p.seller);
  const byMethod = sale && sale.byMethod && typeof sale.byMethod === 'object' ? sale.byMethod : null;
  const refundableOf = () => {
    if (sale && sale.refundable != null) return round2(sale.refundable);
    if (p.refundable != null) return round2(p.refundable);
    if (p.refundableTotal != null) return round2(p.refundableTotal);
    return refundableTotal(lines);
  };
  return {
    allowed: p.allowed !== false,
    reasonCode: p.reasonCode || p.code || (reasonIsCode ? p.reason : null),
    reasonText: p.message || p.reasonText || (!reasonIsCode && typeof p.reason === 'string' ? p.reason : ''),
    canRequest: p.canRequest !== false,
    seller,
    amount: Math.abs(amount),
    refundable: refundableOf(),
    alreadyRefunded: Math.abs(round2(p.refundedTotal != null ? p.refundedTotal : (sale && sale.refunded != null ? sale.refunded : p.alreadyRefunded))),
    total: round2(sale && sale.paid != null ? sale.paid : (p.saleTotal != null ? p.saleTotal : p.paidTotal)),
    lines,
    methods: normaliseMethods(p.methods || p.byMethod || p.perMethod),
    // What's left to refund on each way the sale was paid (an override can
    // only move money between these; a card never gets back more than it paid).
    paidMethods: byMethod
      ? normaliseMethods(Object.keys(byMethod).reduce((o, k) => { o[k] = byMethod[k] && byMethod[k].refundable != null ? byMethod[k].refundable : 0; return o; }, {}))
      : normaliseMethods(p.paidMethods || p.payments),
    recipients: normaliseRecipients(p.recipients || p.reversals || p.from),
    fee: normaliseFee(p.fee || p.stripeFee || p.feeRecovery, seller),
    stock: Array.isArray(p.stock) ? p.stock : null,
    loyalty: p.loyalty || null,
    client: p.client || null,
  };
}

/** "2 items go back in stock", from the preview's stock rows ({ qty, restock }). */
export function serverStockLine(stock) {
  if (!Array.isArray(stock)) return '';
  const qty = stock.filter((x) => x && x.restock).reduce((s, x) => s + (Number(x.qty) > 0 ? Number(x.qty) : 1), 0);
  if (!qty) return '';
  return `${qty} ${qty === 1 ? 'item goes' : 'items go'} back in stock`;
}

/** What went back, by method, from the create response's rows (skips failed rows). */
export function methodsFromRows(rows) {
  if (!Array.isArray(rows)) return [];
  const byMethod = {};
  rows.forEach((r) => {
    if (!r || String(r.status || 'succeeded').toLowerCase() === 'failed') return;
    const m = methodKey(r.method);
    byMethod[m] = round2((byMethod[m] || 0) + Math.abs(num(r.amount)));
  });
  return normaliseMethods(byMethod);
}

/**
 * Why a refund can't go ahead, in the panel's terms:
 *   { kind: 'seller', name, userId }  a self-employed stylist's sale, caller isn't them
 *   { kind: 'permission' }            salon sale, caller lacks `refunds`
 *   { kind: 'nothing' }               nothing left to refund
 *   { kind: 'other', text }           any other refusal, server's words
 * or null when it can.
 */
export function refusalOf(preview, callerId) {
  if (!preview) return null;
  const code = String(preview.reasonCode || '');
  const seller = preview.seller;
  const notMine = seller && seller.kind === 'self_employed' && seller.userId && callerId && String(seller.userId) !== String(callerId);
  if (preview.allowed) {
    if (preview.refundable != null && preview.refundable <= 0 && preview.lines.length) return { kind: 'nothing' };
    return null;
  }
  if (/seller|self.?employed|not_owner|se_sale/i.test(code) || (notMine && !/perm|capab/i.test(code))) {
    return { kind: 'seller', name: (seller && seller.name) || null, userId: (seller && seller.userId) || null };
  }
  if (/perm|capab|forbidden|not_allowed|role/i.test(code)) return { kind: 'permission', text: preview.reasonText || '' };
  if (/nothing|fully|already|no_refundable/i.test(code)) return { kind: 'nothing' };
  return { kind: 'other', text: preview.reasonText || '' };
}

/** "This is Jo's sale. Only Jo can refund it." */
export function sellerMessage(name) {
  if (!name) return "This is a self-employed stylist's sale. Only they can refund it.";
  return `This is ${name}'s sale. Only ${name} can refund it.`;
}

export const askLabel = (name) => (name ? `Ask ${name} to refund` : 'Ask them to refund');

export const PERMISSION_MESSAGE = 'Refunding salon sales needs the Refund sales permission. The owner and managers have it, and the owner can give it to others in Team, Permissions.';

// ── Reasons ───────────────────────────────────────────────────────

export const REFUND_REASONS = [
  'Client not happy',
  'Service not finished',
  'Product returned',
  'Charged by mistake',
  'Charged twice',
  'Other',
];

// ── After the refund ──────────────────────────────────────────────

/** What staff need to do or tell the client, one line per method. */
export function afterLines(methods) {
  return (methods || []).map((m) => {
    if (m.method === 'card') return `${money(m.amount)} back to the card. It usually reaches the client in 5 to 10 working days.`;
    if (m.method === 'cash') return `Give the client ${money(m.amount)} in cash from the till.`;
    if (m.method === 'voucher') return `${money(m.amount)} is back on their gift card.`;
    return `${money(m.amount)} refunded by ${m.label.toLowerCase()}.`;
  });
}

/**
 * "Refund receipt sent to jo@example.com", or '' when we can't say. Reads the
 * create response's receipt ({ sent, email }) or receiptSentTo; when the
 * response is silent the engine emails the client (spec "Receipts"), so the
 * client's own email is named.
 */
export function receiptLine(res, clientEmail) {
  const r = (res && res.receipt) || null;
  if (r && r.sent === false) return '';
  const email = (r && (r.email || r.to)) || (res && res.receiptSentTo) || clientEmail || '';
  if (!email || /@beuapp\.com$/i.test(email)) return '';
  return `Refund receipt sent to ${email}`;
}

/** The server's words when it has them; calm fallbacks otherwise. */
export function refundErrorText(e, fallback) {
  if (e && e.message === 'Network Error') return "Couldn't reach Beu. Check your connection and try again.";
  const d = e && e.response && e.response.data;
  const server = d && ((d.error && (d.error.responseMessage || d.error.message)) || d.message || d.responseMessage);
  if (server) return String(server);
  if (e && e.response && e.response.status === 404) return "Refunds aren't switched on here yet.";
  return (e && e.message && e.message !== 'Request failed' ? e.message : '') || fallback || 'Something went wrong. Please try again.';
}

/**
 * A refund row for the transaction detail (spec refunds[] row). Returns the
 * bits the panel shows: when, how much, how, who, why, what, shares back.
 */
export function refundRowView(r) {
  if (!r) return null;
  const lines = Array.isArray(r.lines) ? r.lines : [];
  return {
    id: r.refundId || r._id || r.id || String(r.at || ''),
    at: r.at || r.recordedAt || r.createdAt || null,
    amount: Math.abs(round2(r.amount)),
    method: METHOD_LABEL[methodKey(r.method)] || 'Other',
    status: String(r.status || 'succeeded').toLowerCase(),
    by: r.byName || (r.by && typeof r.by === 'object' ? r.by.name : null) || null,
    reason: r.reason || '',
    note: r.note || '',
    source: r.source || 'beu',
    what: lines.map((l) => l.name || LINE_KIND_LABEL[String(l.kind || '').toLowerCase()] || 'Item').filter(Boolean).join(', '),
    restocked: lines.some((l) => l.restocked),
    reversals: (Array.isArray(r.reversals) ? r.reversals : [])
      .filter((v) => v && String(v.status || 'succeeded').toLowerCase() !== 'failed')
      .map((v) => ({ name: v.userName || v.name || null, role: isSalonRole(v.role) ? 'salon' : 'stylist', amount: Math.abs(round2(v.amount)) }))
      .filter((v) => v.amount > 0),
    fee: r.feeRecovery && num(r.feeRecovery.amount) > 0 ? { amount: Math.abs(round2(r.feeRecovery.amount)), name: r.feeRecovery.fromUserName || r.feeRecovery.fromName || null } : null,
  };
}
